import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, ExternalLink, ArrowRight, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SectionEquivalenciesTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comparativeData, setComparativeData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [expandedSummaries, setExpandedSummaries] = useState({});
  const [summaryLoading, setSummaryLoading] = useState({});
  const [summaries, setSummaries] = useState({});
  const navigate = useNavigate();

  // Fetch comparative matrix data on component mount
  useEffect(() => {
    const fetchComparativeData = async () => {
      try {
        setDataLoading(true);
        const response = await fetch(`${API_URL}/api/comparative-matrix`);
        if (!response.ok) {
          throw new Error('Failed to fetch comparative matrix data');
        }
        const data = await response.json();
        setComparativeData(data);
      } catch (error) {
        console.error('Error fetching comparative data:', error);
        toast.error('Failed to load comparative matrix data');
      } finally {
        setDataLoading(false);
      }
    };

    fetchComparativeData();
  }, []);

  const searchSections = () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    
    // Clear previous summaries and expanded states when searching
    setExpandedSummaries({});
    setSummaries({});
    setSummaryLoading({});
    
    const query = searchQuery.toLowerCase();
    const matchedSections = [];

    // Search through all articles and sections in comparative data
    comparativeData.forEach((article) => {
      article.sections.forEach((section) => {
        const title9184 = section.title9184?.toLowerCase() || '';
        const title12009 = section.title12009?.toLowerCase() || '';
        const sec9184 = section.sec9184?.toLowerCase() || '';
        const sec12009 = section.sec12009?.toLowerCase() || '';
        
        if (title9184.includes(query) || title12009.includes(query) || 
            sec9184.includes(query) || sec12009.includes(query)) {
          matchedSections.push({
            ...section,
            article: article.article,
            articleTitle9184: article.title9184,
            articleTitle12009: article.title12009
          });
        }
      });
    });

    // Sort results by section number in ascending numerical order
    // Prioritize exact matches first, then sort numerically
    matchedSections.sort((a, b) => {
      // Helper function to extract numeric value from section number
      const getNumericValue = (sec) => {
        if (!sec || sec === '-') return Infinity;
        // Extract just the number part (e.g., "3" from "3" or "3.1")
        const num = parseFloat(sec.replace(/[^\d.]/g, ''));
        return isNaN(num) ? Infinity : num;
      };
      
      // Get the primary section number for sorting (prefer RA 9184, fallback to RA 12009)
      const secA = a.sec9184 !== '-' ? a.sec9184 : a.sec12009;
      const secB = b.sec9184 !== '-' ? b.sec9184 : b.sec12009;
      
      const numA = getNumericValue(secA);
      const numB = getNumericValue(secB);
      
      // Check for exact match with query
      const queryNum = parseFloat(query);
      if (!isNaN(queryNum)) {
        const isExactA = numA === queryNum;
        const isExactB = numB === queryNum;
        if (isExactA && !isExactB) return -1;
        if (isExactB && !isExactA) return 1;
      }
      
      // Sort by numeric value
      return numA - numB;
    });

    setResults(matchedSections);
    setLoading(false);
    
    if (matchedSections.length === 0) {
      toast.info('No matching sections found');
    }
  };

  // Navigate to a section in the Library
  const navigateToSection = async (sectionNumber, referenceType) => {
    if (sectionNumber === '-' || !sectionNumber) {
      toast.info('This section is not present in this law');
      return;
    }
    
    try {
      const response = await api.get(`/sections/find-by-number?section_number=${encodeURIComponent(sectionNumber)}&reference_type=${referenceType}`);
      const data = response.data;
      
      if (data.found) {
        navigate(`/sections/${data.section_id}`);
      } else {
        toast.info(`Section ${sectionNumber} not found in the library`);
      }
    } catch (error) {
      console.error('Error finding section:', error);
      toast.error('Failed to find section');
    }
  };

  // Toggle and fetch summary for a section comparison
  const toggleSummary = async (index, section) => {
    const key = `${index}`;
    
    // If already expanded, just collapse
    if (expandedSummaries[key]) {
      setExpandedSummaries(prev => ({ ...prev, [key]: false }));
      return;
    }
    
    // Expand and fetch summary if not already loaded
    setExpandedSummaries(prev => ({ ...prev, [key]: true }));
    
    if (!summaries[key]) {
      setSummaryLoading(prev => ({ ...prev, [key]: true }));
      try {
        // Pass titles along with section numbers for transposed/combined sections
        const params = new URLSearchParams({
          sec9184: section.sec9184 || '-',
          sec12009: section.sec12009 || '-',
          status: section.status || 'modified',
          title9184: section.title9184 || section.title_9184 || '',
          title12009: section.title12009 || section.title_12009 || ''
        });
        const response = await api.get(`/sections/compare-summary?${params.toString()}`);
        setSummaries(prev => ({ ...prev, [key]: response.data.summary }));
      } catch (error) {
        console.error('Error fetching summary:', error);
        setSummaries(prev => ({ ...prev, [key]: 'Unable to load summary. Please try again.' }));
      } finally {
        setSummaryLoading(prev => ({ ...prev, [key]: false }));
      }
    }
  };

  // Show loading state while fetching data
  if (dataLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-slate-600">Loading comparative matrix data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="w-5 h-5 text-emerald-600" />
          Section Equivalencies Finder
        </CardTitle>
        <CardDescription>
          Search for equivalent sections between RA 9184 and RA 12009. Click on any section to view its full text in the Library. Click "View Summary" to see the brief overview of the changes or updates.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Search Input */}
          <div className="flex gap-2">
            <Input
              placeholder="Enter topic, keyword, or section number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchSections()}
            />
            <Button onClick={searchSections} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{results.length} Section(s) Found</h3>
              <div className="space-y-3">
                {results.map((section, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* RA 9184 Section */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium px-2 py-1 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                              RA 9184
                            </span>
                            {section.sec9184 !== '-' && (
                              <button
                                onClick={() => navigateToSection(section.sec9184, 'ra9184')}
                                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                              >
                                Section {section.sec9184}
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <h4 
                            className={`font-semibold ${section.sec9184 !== '-' ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400' : ''}`}
                            onClick={() => section.sec9184 !== '-' && navigateToSection(section.sec9184, 'ra9184')}
                          >
                            {section.title9184 !== '-' ? section.title9184 : 'Not Present'}
                          </h4>
                          <p className="text-xs text-slate-500">Article {section.article}: {section.articleTitle9184}</p>
                        </div>

                        {/* Arrow or Status */}
                        <div className="hidden md:flex items-center justify-center">
                          <div className="flex flex-col items-center">
                            <ArrowRight className="w-6 h-6 text-emerald-600" />
                            <span className={`text-xs mt-1 px-2 py-1 rounded ${
                              section.status === 'retained' ? 'bg-green-100 text-green-700' :
                              section.status === 'modified' ? 'bg-yellow-100 text-yellow-700' :
                              section.status === 'added' ? 'bg-blue-100 text-blue-700' :
                              section.status === 'transposed' ? 'bg-purple-100 text-purple-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {section.status}
                            </span>
                          </div>
                        </div>

                        {/* RA 12009 Section */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium px-2 py-1 rounded bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300">
                              RA 12009
                            </span>
                            {section.sec12009 !== '-' && (
                              <button
                                onClick={() => navigateToSection(section.sec12009, 'ra12009')}
                                className="text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1"
                              >
                                Section {section.sec12009}
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <h4 
                            className={`font-semibold ${section.sec12009 !== '-' ? 'cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400' : ''}`}
                            onClick={() => section.sec12009 !== '-' && navigateToSection(section.sec12009, 'ra12009')}
                          >
                            {section.title12009 !== '-' ? section.title12009 : 'Not Present'}
                          </h4>
                          <p className="text-xs text-slate-500">Article {section.article}: {section.articleTitle12009}</p>
                        </div>
                      </div>
                      
                      {/* Summary Section */}
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <button
                          onClick={() => toggleSummary(index, section)}
                          className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400"
                        >
                          {expandedSummaries[index] ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              Hide Summary
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              View Summary
                            </>
                          )}
                        </button>
                        
                        {expandedSummaries[index] && (
                          <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg max-h-96 overflow-y-auto">
                            {summaryLoading[index] ? (
                              <div className="flex items-center gap-2 text-slate-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Generating summary...</span>
                              </div>
                            ) : (
                              <div className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">
                                {summaries[index]}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {results.length === 0 && searchQuery && !loading && (
            <div className="text-center py-8 text-slate-500">
              No sections found. Try different keywords.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
